/**
 * Push Notification Service
 *
 * Manages browser push notification permission, FCM token lifecycle,
 * and token storage in Firestore for the Cloud Function to target.
 */

import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import app from '../config/firebase';
import { db } from '../config/firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';
const TOKEN_KEY = 'lucidDock_fcmToken';

class PushNotificationService {
  private messaging: Messaging | null = null;

  private getMessagingInstance(): Messaging | null {
    if (this.messaging) return this.messaging;
    try {
      this.messaging = getMessaging(app);
      return this.messaging;
    } catch {
      return null;
    }
  }

  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  getPermissionState(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  async requestPermission(userId: string): Promise<boolean> {
    if (!this.isSupported()) return false;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    return this.registerToken(userId);
  }

  async registerToken(userId: string): Promise<boolean> {
    if (!VAPID_KEY) return false;
    const messaging = this.getMessagingInstance();
    if (!messaging) return false;

    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!registration) return false;

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (!token) return false;

      const previousToken = localStorage.getItem(TOKEN_KEY);
      if (previousToken && previousToken !== token) {
        await this.removeTokenFromServer(userId, previousToken);
      }

      await this.saveTokenToServer(userId, token);
      localStorage.setItem(TOKEN_KEY, token);
      return true;
    } catch {
      return false;
    }
  }

  async unregisterToken(userId: string): Promise<void> {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      await this.removeTokenFromServer(userId, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  onForegroundMessage(callback: (payload: { title: string; body: string }) => void): () => void {
    const messaging = this.getMessagingInstance();
    if (!messaging) return () => {};

    return onMessage(messaging, (payload) => {
      const title = payload.notification?.title || 'LucidDock';
      const body = payload.notification?.body || '';
      callback({ title, body });
    });
  }

  private async saveTokenToServer(userId: string, token: string): Promise<void> {
    const ref = doc(db, 'pilots', userId);
    await updateDoc(ref, { fcmTokens: arrayUnion(token) });
  }

  private async removeTokenFromServer(userId: string, token: string): Promise<void> {
    try {
      const ref = doc(db, 'pilots', userId);
      await updateDoc(ref, { fcmTokens: arrayRemove(token) });
    } catch { /* token may already be gone */ }
  }
}

export const pushNotificationService = new PushNotificationService();
